import { Form } from "client/Components";
import {
	AppDestinationWindowSelectFormItem
} from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { DestinationWindow } from "client/Api/Apps/common";
import { ExistingTile } from "./common";
import {
	GenericDetailsTileModel,
	GenericUpdateTileModel
} from "client/Api/Workspace/workspace";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile } from "./hooks/useUpdateTile";

export type EditGenericWithDestinationWindowTilePropsBase<TInitialModel extends GenericDetailsTileModelWithDestinationWindow> = {
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	initialModel: TInitialModel;
	mode: ExistingTile;
	onAfterSave: () => void;
};

export type EditGenericWithDestinationWindowTileProps = EditGenericWithDestinationWindowTilePropsBase<GenericDetailsTileModelWithDestinationWindow>
	& {
		requestHandler: (
			model: GenericUpdateTileModelWithDestinationWindow,
			signal: AbortSignal
		) => Promise<void>;
	};

export const EditGenericWithDestinationWindowTile = forwardRef(
	(props: EditGenericWithDestinationWindowTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			isLoading,
			onIsLoadingChange,
			initialModel,
			requestHandler,
			mode,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<GenericWithDestinationEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow
		});

		const formHandler: IFormHandler<GenericWithDestinationEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationConfiguration
		);

		const { updateHandler } = useUpdateTile<GenericUpdateTileModelWithDestinationWindow>(
			formHandler,
			requestHandler,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			() => ({ updateHandler }),
			[updateHandler]
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</Form>
		);
	}
);
EditGenericWithDestinationWindowTile.displayName = "EditGenericWithDestinationWindowTile";

type GenericWithDestinationEditFormData = EditTileFromData<GenericUpdateTileModelWithDestinationWindow>;

type GenericDetailsTileModelWithDestinationWindow = GenericDetailsTileModel
	& {
		destinationWindow: DestinationWindow;
	};

type GenericUpdateTileModelWithDestinationWindow = GenericUpdateTileModel
	& {
		destinationWindow: DestinationWindow;
	};

const validationConfiguration: IValidationConfiguration<GenericWithDestinationEditFormData> =
	createValidationConfiguration<GenericWithDestinationEditFormData>();

validationConfiguration
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);